import type { TimeOfDay } from './EnvironmentState';

/**
 * Manual/derived sun model (spec §5.2). Converts a time of day (plus site latitude and day of year)
 * into a sun azimuth/elevation, then into a light direction and intensity. All functions are pure so
 * the math is unit-testable independent of the renderer.
 */

export interface Vec3 {
  x: number;
  y: number;
  z: number;
}

export interface SunPosition {
  /** Compass azimuth in degrees, clockwise from north (0 = N, 90 = E, 180 = S, 270 = W). */
  azimuthDegrees: number;
  /** Elevation above the horizon in degrees (negative below horizon / night). */
  elevationDegrees: number;
}

export interface SunLighting {
  /** Unit direction the light travels (from the sun toward the scene). */
  direction: Vec3;
  /** Direct light intensity 0–max. */
  intensity: number;
  /** Ambient/fill intensity that persists after sunset. */
  ambientIntensity: number;
  azimuthDegrees: number;
  elevationDegrees: number;
}

const DEG = Math.PI / 180;

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

/** Solar declination in degrees for a given day of year (1–365). */
export function solarDeclinationDegrees(dayOfYear: number): number {
  return 23.44 * Math.sin(2 * Math.PI * (dayOfYear - 81) / 365);
}

/**
 * Compute the sun's azimuth/elevation from the time of day using a simplified solar-position model.
 */
export function computeSunPosition(
  time: TimeOfDay,
  latitudeDegrees: number,
  dayOfYear: number,
): SunPosition {
  const solarHour = time.hour + time.minute / 60;
  const hourAngle = 15 * (solarHour - 12); // degrees, 0 at solar noon
  const lat = latitudeDegrees * DEG;
  const decl = solarDeclinationDegrees(dayOfYear) * DEG;
  const H = hourAngle * DEG;

  const sinElev = Math.sin(lat) * Math.sin(decl) + Math.cos(lat) * Math.cos(decl) * Math.cos(H);
  const elevation = Math.asin(clamp(sinElev, -1, 1));

  const cosElev = Math.cos(elevation);
  let azimuth: number;
  if (cosElev < 1e-6) {
    azimuth = 180;
  } else {
    const cosAz = (Math.sin(decl) - Math.sin(elevation) * Math.sin(lat)) / (cosElev * Math.cos(lat));
    azimuth = Math.acos(clamp(cosAz, -1, 1)) / DEG;
    if (hourAngle > 0) azimuth = 360 - azimuth; // afternoon → west side
  }

  return { azimuthDegrees: azimuth, elevationDegrees: elevation / DEG };
}

/**
 * When the sun crosses the horizon on a given day, in minutes from local midnight. Derived from the
 * same declination model as {@link computeSunPosition}, so the times always agree with the elevation
 * the sun model reports — the HUD paints its sky ribbon from these.
 */
export interface DaylightWindow {
  /** Minutes from midnight, or `null` when the sun never rises or never sets. */
  sunriseMinutes: number | null;
  sunsetMinutes: number | null;
  /** Midnight sun: the sun stays above the horizon all day. */
  polarDay: boolean;
  /** Polar night: the sun never clears the horizon. */
  polarNight: boolean;
}

/** Solve the sun model for elevation = 0 to get sunrise/sunset for a latitude and day of year. */
export function daylightWindow(latitudeDegrees: number, dayOfYear: number): DaylightWindow {
  const lat = clamp(latitudeDegrees, -89.9, 89.9) * DEG;
  const decl = solarDeclinationDegrees(dayOfYear) * DEG;
  // Elevation 0 in the model above reduces to cos(H) = -tan(lat)·tan(decl).
  const cosH = -Math.tan(lat) * Math.tan(decl);
  if (cosH <= -1) {
    return { sunriseMinutes: null, sunsetMinutes: null, polarDay: true, polarNight: false };
  }
  if (cosH >= 1) {
    return { sunriseMinutes: null, sunsetMinutes: null, polarDay: false, polarNight: true };
  }
  const halfDayHours = Math.acos(cosH) / DEG / 15;
  return {
    sunriseMinutes: (12 - halfDayHours) * 60,
    sunsetMinutes: (12 + halfDayHours) * 60,
    polarDay: false,
    polarNight: false,
  };
}

/** Unit vector pointing from the scene origin toward the sun. */
export function sunDirectionToSun(position: SunPosition): Vec3 {
  const az = position.azimuthDegrees * DEG;
  const elev = position.elevationDegrees * DEG;
  const cosElev = Math.cos(elev);
  return {
    x: cosElev * Math.sin(az),
    y: Math.sin(elev),
    z: cosElev * Math.cos(az),
  };
}

/** Unit vector for the direction sunlight travels (toward the scene). */
export function sunLightDirection(position: SunPosition): Vec3 {
  const toSun = sunDirectionToSun(position);
  return { x: -toSun.x, y: -toSun.y, z: -toSun.z };
}

/**
 * Map elevation to a direct light intensity. Zero below the horizon, rising with elevation and
 * softened near the horizon (golden-hour dimming).
 */
export function sunIntensity(elevationDegrees: number, maxIntensity = 1): number {
  if (elevationDegrees <= 0) return 0;
  const t = Math.sin(elevationDegrees * DEG);
  return maxIntensity * clamp(t, 0, 1);
}

/** Resolve full lighting parameters for a time of day. */
export function computeSunLighting(
  time: TimeOfDay,
  latitudeDegrees: number,
  dayOfYear: number,
  maxIntensity = 1,
): SunLighting {
  const position = computeSunPosition(time, latitudeDegrees, dayOfYear);
  const intensity = sunIntensity(position.elevationDegrees, maxIntensity);
  // Retain a soft ambient fill even at night (moon/skylight) so the scene never goes fully black.
  const ambientIntensity = 0.15 + 0.35 * (intensity / maxIntensity);
  return {
    direction: sunLightDirection(position),
    intensity,
    ambientIntensity,
    azimuthDegrees: position.azimuthDegrees,
    elevationDegrees: position.elevationDegrees,
  };
}
